import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { ArticleType } from "@/types/enums";
import type { AdminArticleFilters } from "@/types/article";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";

interface ArticleFiltersProps {
  filters: AdminArticleFilters;
  onFiltersChange: (filters: AdminArticleFilters) => void;
}

function getActiveFilterCount(filters: AdminArticleFilters): number {
  let count = 0;
  if (filters.type) count += 1;
  if (filters.publishedOnly !== undefined) count += 1;
  return count;
}

export function ArticleFiltersComponent({
  filters,
  onFiltersChange,
}: ArticleFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.searchQuery || "");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    const normalizedSearch = debouncedSearch || undefined;
    if (normalizedSearch !== filters.searchQuery) {
      onFiltersChange({
        ...filters,
        searchQuery: normalizedSearch,
      });
    }
  }, [debouncedSearch, filters, onFiltersChange]);

  const hasActiveFilters = Boolean(
    filters.searchQuery || filters.type || filters.publishedOnly !== undefined
  );
  const activeFilterCount = getActiveFilterCount(filters);

  const clearFilters = () => {
    setSearchInput("");
    onFiltersChange({
      searchQuery: undefined,
      type: undefined,
      publishedOnly: undefined,
    });
  };

  const filterSelects = (
    <>
      <Select
        value={filters.type || "ALL"}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            type: value === "ALL" ? undefined : (value as ArticleType),
          })
        }
      >
        <SelectTrigger
          className="w-full md:w-[160px]"
          aria-label="Filter by type"
        >
          <SelectValue placeholder="유형 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 유형</SelectItem>
          <SelectItem value={ArticleType.BLOG}>BLOG</SelectItem>
          <SelectItem value={ArticleType.PREMIUM}>PREMIUM</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={
          filters.publishedOnly === undefined
            ? "ALL"
            : filters.publishedOnly
              ? "PUBLISHED"
              : "DRAFT"
        }
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            publishedOnly:
              value === "ALL"
                ? undefined
                : value === "PUBLISHED",
          })
        }
      >
        <SelectTrigger
          className="w-full md:w-[160px]"
          aria-label="Filter by status"
        >
          <SelectValue placeholder="상태 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 상태</SelectItem>
          <SelectItem value="PUBLISHED">발행됨</SelectItem>
          <SelectItem value="DRAFT">임시저장</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          onClick={clearFilters}
          className="w-full md:w-auto min-h-[44px] md:min-h-0"
          aria-label="Clear all filters"
        >
          <X className="mr-2 h-4 w-4" />
          초기화
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="제목 또는 작성자로 검색..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 h-11 md:h-9"
              aria-label="Search articles"
            />
          </div>

          <Button
            variant="outline"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="md:hidden h-11 min-w-[44px] relative"
            aria-label="필터 토글"
            aria-expanded={mobileFiltersOpen}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>

        <div className="hidden md:contents">{filterSelects}</div>
      </div>

      <div
        className={cn(
          "md:hidden flex flex-col gap-3",
          !mobileFiltersOpen && "hidden"
        )}
      >
        {filterSelects}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">필터:</span>
          {filters.searchQuery && (
            <Badge variant="secondary" className="gap-1 pr-1">
              검색: &quot;{filters.searchQuery}&quot;
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  onFiltersChange({ ...filters, searchQuery: undefined });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="검색 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.type && (
            <Badge variant="secondary" className="gap-1 pr-1">
              유형: {filters.type}
              <button
                type="button"
                onClick={() =>
                  onFiltersChange({ ...filters, type: undefined })
                }
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="유형 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.publishedOnly !== undefined && (
            <Badge variant="secondary" className="gap-1 pr-1">
              상태: {filters.publishedOnly ? "발행됨" : "임시저장"}
              <button
                type="button"
                onClick={() =>
                  onFiltersChange({ ...filters, publishedOnly: undefined })
                }
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="상태 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            전체 초기화
          </button>
        </div>
      )}
    </div>
  );
}
